import { create } from 'zustand'
import dayjs, { DayjsInput } from 'utils/dayjs'
import { EventService } from '../components/modules/Calendar/services/eventServices'
import { DATE_FORMAT } from '~constants'
import { ArtistProps, EventAPIResponse } from 'components/modules/Calendar/types'
import { EventsByDate, EventsByMonth, FormattedEvent } from 'components/modules/Calendar/types/Event'
import { EventFormatter } from 'components/modules/Calendar/utils/EventFormatter'
import { MonthProps } from '~types'

const eventFormatter = new EventFormatter()

// Helper function to format the month
const formatMonth = (date: DayjsInput): MonthProps => {
  const d = dayjs?.isDayjs(date) ? date : dayjs(date as string)
  if (!d) return { label: '', value: '', shortLabel: '' }
  return {
    label: d.format(DATE_FORMAT.MONTH_YEAR_LABEL),
    value: d.format(DATE_FORMAT.MONTH_YEAR_VALUE),
    shortLabel: d.format(DATE_FORMAT.MONTH_YEAR_SHORT)
  }
}

// Types for the events store
interface EventsMonthParams {
  startDate: string
  endDate: string
  artistIds: string
}

interface EventsState {
  startDate: DayjsInput
  endDate: DayjsInput
  selectedDate: DayjsInput
  selectedMonth: MonthProps
  artists: ArtistProps[]
  loading: boolean
  errors: string | null
  events: EventAPIResponse[]
  eventsByDate: EventsByDate | null
  eventsByMonth: EventsByMonth
  formattedEvents: FormattedEvent[]
  activeMonthList: string[]
  eventsMonthParams: EventsMonthParams | null
  activeShowId?: string | null
  selectedEvent?: {
    events: FormattedEvent[]
    artistDetail: ArtistProps
  } | null

  setSelectedDate: (date: DayjsInput) => void
  setStartDate: (date: DayjsInput) => void
  setEndDate: (date: DayjsInput) => void
  setArtists: (artists: ArtistProps[]) => void
  setSelectedEvent: (event: { events: FormattedEvent[]; artistDetail: ArtistProps } | null) => void
  fetchEvents: (startDate: DayjsInput, endDate: DayjsInput, artists: ArtistProps[]) => Promise<void>
  setActiveShowId: (id: string | null) => void
}

export const useEventsStore = create<EventsState>((set, get) => ({
  startDate: dayjs(),
  endDate: dayjs(),
  selectedDate: dayjs(),
  selectedMonth: formatMonth(dayjs()),
  artists: [],
  loading: true,
  errors: null,
  events: [],
  activeMonthList: [],
  eventsMonthParams: null,
  eventsByDate: null,
  eventsByMonth: {},
  formattedEvents: [],
  selectedEvent: null,

  // Setters for various states
  setStartDate: (date: DayjsInput) => set({ startDate: dayjs(date as string) }),

  setSelectedDate: (date: DayjsInput) =>
    set({
      selectedDate: dayjs(date as string),
      selectedMonth: formatMonth(date)
    }),

  setEndDate: (date: DayjsInput) => set({ endDate: dayjs(date as string) }),

  setArtists: (artists: ArtistProps[]) => set({ artists }),

  setSelectedEvent: event => set({ selectedEvent: event }),

  setActiveShowId: (id: string | null) => set({ activeShowId: id }),

  // Function to fetch events from the API
  fetchEvents: async (startDate, endDate, artists) => {
    const formattedStart = dayjs(startDate as string).format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
    const formattedEnd = dayjs(endDate as string).format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
    const artistIds = artists.length
      ? artists
          .map(a => a?.artistId ?? null)
          .sort()
          .filter(id => id !== null)
          .join(',')
      : ''
    const newParams: EventsMonthParams = { startDate: formattedStart, endDate: formattedEnd, artistIds }

    // Check if the new params are different from the last params
    const lastParams = get().eventsMonthParams
    if (
      lastParams &&
      newParams.startDate === lastParams.startDate &&
      newParams.endDate === lastParams.endDate &&
      newParams.artistIds === lastParams.artistIds
    ) {
      set({ loading: false })
      return
    }

    // Update state and start fetching events
    set({ loading: true, errors: null, eventsMonthParams: newParams })

    try {
      const eventService = new EventService()
      const { events, activeMonthList } = await eventService.getEventsFromEventEngine(
        formattedStart,
        formattedEnd,
        artistIds
      )
      if (events.length == 0) {
        set({
          loading: false
        })
        return
      }
      const { eventsByDate, eventsByMonth, formattedEvents } = eventFormatter.processEventEngineResponse(events)
      set({
        events,
        activeMonthList: activeMonthList || [],
        eventsByDate,
        eventsByMonth,
        formattedEvents,
        loading: false
      })
    } catch (error) {
      console.error(error)
      set({ errors: 'Failed to fetch events. Please try again later.', loading: false })
    }
  }
}))
